// ============================================
// COST-MATH. Canonical single-source-of-truth cost engine (Wave 3)
// ============================================
// Pure functions. No I/O, no AI, no React, no Dexie.
// Order of operations + 7 invariants per docs/costing-integrity-plan.md.
// All internal math uses integer cents to avoid IEEE-754 drift; public
// outputs are 2dp dollar numbers.

import { GST_RATE, HBCF_THRESHOLD_INCGST, STATUTORY_INSURANCE_RE } from './cost-rules.js';

// ---------------- parseAmount (DATA-9: canonical home) ----------------
// why: safely parse a money amount from any input: handles strings with
// commas, dollar signs, null, undefined, NaN. Used everywhere in the cost
// engine to avoid silent NaN bugs from raw parseFloat on AI-generated
// "$1,234.50" strings.
export function parseAmount(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return isFinite(value) ? value : 0;
    const cleaned = String(value).replace(/[$,\s]/g, '');
    const parsed = parseFloat(cleaned);
    return isFinite(parsed) ? parsed : 0;
}

// ---------------- constants ----------------
const CENTS = 100;
const EPSILON_CENTS = 1; // 1c tolerance for equality checks
const LOGICAL_ROUND_BIG_THRESHOLD = 10_000; // ≥$10k → round to $100, else $10
const LOGICAL_ROUND_BIG_UNIT = 100;
const LOGICAL_ROUND_SMALL_UNIT = 10;
const DEFAULT_ITEM_ROUND_UNIT = 10;

// ---------------- helpers ----------------
const toCents = (dollars) => Math.round(parseAmount(dollars) * CENTS);
const toDollars = (cents) => Math.round(cents) / CENTS;
const clampPct = (pct) => {
    const n = parseAmount(pct);
    return n < 0 ? 0 : n; // negative pcts make no sense
};

function roundUpToUnitCents(valueCents, unitDollars = DEFAULT_ITEM_ROUND_UNIT) {
    const unitCents = Math.max(1, Math.round(parseAmount(unitDollars) * CENTS));
    if (!Number.isFinite(valueCents) || valueCents <= 0) return 0;
    return Math.ceil(valueCents / unitCents) * unitCents;
}

/**
 * Round a client-facing inclusive-GST line amount up to a clean quote item.
 * RJ BIRD wants item prices that do not carry loose cents; Stage 4/5 use a
 * $10 unit unless a future tenant config explicitly changes it.
 *
 * @param {number|string} amountIncGst
 * @param {number|string} [unitDollars=10]
 * @returns {number}
 */
export function roundUpItemIncGst(amountIncGst, unitDollars = DEFAULT_ITEM_ROUND_UNIT) {
    return toDollars(roundUpToUnitCents(toCents(amountIncGst), unitDollars));
}

/**
 * Split one fixed dollar amount into deterministic integer-cent allocations.
 * Earlier items receive any remainder cents so the returned values always sum
 * to the configured amount exactly.
 *
 * @param {number|string} totalAmount
 * @param {number} itemCount
 * @returns {number[]}
 */
export function allocateFixedAmountCents(totalAmount, itemCount) {
    const count = Math.max(1, Math.trunc(Number(itemCount) || 1));
    const totalCents = toCents(totalAmount);
    const baseCents = Math.trunc(totalCents / count);
    const remainderCents = totalCents - baseCents * count;
    const remainderStep = Math.sign(remainderCents);

    return Array.from({ length: count }, (_, index) => (
        baseCents + (index < Math.abs(remainderCents) ? remainderStep : 0)
    ));
}

/**
 * @typedef {Object} LineItemInputs
 * @property {string} [id]
 * @property {string} [title]
 * @property {number|string} [materialsCost]
 * @property {number|string} [labourCost]
 * @property {number|string} [equipmentCost]
 * @property {number|string} [subcontractCost]
 * @property {number|string} [otherCost]
 * @property {number|string} [contingencyPct]
 * @property {number|string} [overheadPct]
 * @property {number|string} [contingencyAmountOverride]
 * @property {number|string} [overheadAmountOverride]
 * @property {number|string} [marginPct]
 * @property {number|string} [amountOverride]
 * @property {number|string} [amountOverrideIncGst]
 * @property {boolean} [amountLocked]
 * @property {boolean} [provisionalSum]
 * @property {boolean} [hbcfApplicable]
 * @property {boolean} [excludeFromHbcfEligibility]
 * @property {boolean} [gstExempt]  DATA-2: statutory-insurance lines (HBCF) attract no GST
 * @property {boolean} [excludeFromFixedAllocations] statutory/admin rows should not dilute fixed job allowances
 */

/**
 * @typedef {Object} LineItemTotals
 * @property {string|undefined} id
 * @property {number} direct
 * @property {number} contingencyAmount
 * @property {number} overheadAmount
 * @property {number} totalCost
 * @property {number} marginPct
 * @property {number} marginAmount
 * @property {number} exGst
 * @property {number} gst
 * @property {number} incGst
 * @property {number} amount
 * @property {boolean} amountLocked
 * @property {boolean} provisionalSum
 * @property {Object} trace
 */

/**
 * @typedef {Object} ProjectCostConfig
 * @property {number} [contingencyPct] default 10
 * @property {number} [overheadPct] default 8
 * @property {Object} [overhead]
 * @property {'percent'|'fixed'} [overhead.mode]
 * @property {number} [overhead.fixedAmount] split across itemCount
 * @property {Object} [contingency] present only for a fixed rule; a percent rule uses contingencyPct
 * @property {'fixed'} [contingency.mode]
 * @property {number} [contingency.fixedAmount] split across itemCount, like overhead
 * @property {boolean} [gstInclusive] display-only
 * @property {number} [gstRate] default GST_RATE (0.10)
 * @property {Array<{min?:number,max?:number,rate:number}>} [hbcf.rateBands]
 * @property {Array<{maxValue:number|null,marginPct:number}>} [marginBrackets]
 * @property {boolean} [roundItemIncGst] round client-facing inc-GST line amounts to clean item prices
 * @property {number} [itemRoundUnit] dollar unit for roundItemIncGst; default $10
 */

/**
 * @typedef {Object} ProjectTotals
 * @property {LineItemTotals[]} items
 * @property {number} subtotalExGst
 * @property {number} subtotalIncGst
 * @property {number} gstAmount
 * @property {number} totalExGst
 * @property {number} totalIncGst
 * @property {Object} trace
 * @property {{ok:boolean,failures:string[]}} invariants
 */

// ---------------- line item ----------------
/**
 * Compute a single line-item's derived totals.
 * @param {LineItemInputs} inputs
 * @param {ProjectCostConfig} [config]
 * @param {{itemCount?:number, projectEstimate?:number, fixedOverheadCents?:number, fixedContingencyCents?:number}} [ctx]
 * @returns {LineItemTotals}
 */
export function calculateLineItem(inputs = {}, config = {}, ctx = {}) {
    const itemCount = Math.max(1, Number(ctx.itemCount) || 1);
    const gstRate = Number.isFinite(config.gstRate) ? config.gstRate : GST_RATE;
    const provisionalSum = !!inputs.provisionalSum;
    const amountLocked = !!inputs.amountLocked;

    const materialsCents   = toCents(inputs.materialsCost);
    const labourCents      = toCents(inputs.labourCost);
    const equipmentCents   = toCents(inputs.equipmentCost);
    const subcontractCents = toCents(inputs.subcontractCost);
    const otherCents       = toCents(inputs.otherCost);
    const directCents = materialsCents + labourCents + equipmentCents + subcontractCents + otherCents;

    const contingencyPct = clampPct(inputs.contingencyPct ?? config.contingencyPct ?? 10);
    const overheadPct    = clampPct(inputs.overheadPct ?? config.overheadPct ?? 8);
    let marginPct        = clampPct(inputs.marginPct ?? 0);
    if (provisionalSum) marginPct = 0; // provisional sums carry no margin

    const hasContingencyOverride = inputs.contingencyAmountOverride !== null
        && inputs.contingencyAmountOverride !== undefined
        && Number.isFinite(Number(inputs.contingencyAmountOverride));
    const hasOverheadOverride = inputs.overheadAmountOverride !== null
        && inputs.overheadAmountOverride !== undefined
        && Number.isFinite(Number(inputs.overheadAmountOverride));
    // A fixed contingency is one amount per quote, split across the costed
    // items exactly like a fixed overhead. Twin of price-items.js.
    let contingencyCents;
    if (hasContingencyOverride) {
        contingencyCents = toCents(inputs.contingencyAmountOverride);
    } else if (config.contingency?.mode === 'fixed') {
        const fixedTotalCents = toCents(config.contingency.fixedAmount);
        contingencyCents = Number.isInteger(ctx.fixedContingencyCents)
            ? ctx.fixedContingencyCents
            : Math.round(fixedTotalCents / itemCount);
    } else {
        contingencyCents = Math.round(directCents * contingencyPct / 100);
    }

    let overheadCents;
    if (hasOverheadOverride) {
        overheadCents = toCents(inputs.overheadAmountOverride);
    } else if (config.overhead?.mode === 'fixed') {
        const fixedTotalCents = toCents(config.overhead.fixedAmount);
        overheadCents = Number.isInteger(ctx.fixedOverheadCents)
            ? ctx.fixedOverheadCents
            : Math.round(fixedTotalCents / itemCount);
    } else {
        overheadCents = Math.round(directCents * overheadPct / 100);
    }

    const totalCostCents = directCents + contingencyCents + overheadCents;

    let marginAmountCents;
    let exGstCents;
    let gstCents;
    let incGstCents;
    const incOverrideProvided = inputs.amountOverrideIncGst != null && inputs.amountOverrideIncGst !== '';
    const exOverrideProvided = inputs.amountOverride != null && inputs.amountOverride !== '';
    const overrideProvided = incOverrideProvided || exOverrideProvided;
    const gstExempt = !!inputs.gstExempt;

    if (incOverrideProvided) {
        incGstCents = toCents(inputs.amountOverrideIncGst);
        if (gstExempt) {
            exGstCents = incGstCents;
            gstCents = 0;
        } else {
            exGstCents = Math.round(incGstCents / (1 + gstRate));
            gstCents = incGstCents - exGstCents;
        }
        marginAmountCents = exGstCents - totalCostCents;
        marginPct = totalCostCents > 0 ? (marginAmountCents / totalCostCents) * 100 : 0;
    } else if (exOverrideProvided) {
        exGstCents = toCents(inputs.amountOverride);
        marginAmountCents = exGstCents - totalCostCents;
        marginPct = totalCostCents > 0 ? (marginAmountCents / totalCostCents) * 100 : 0;
    } else {
        marginAmountCents = Math.round(totalCostCents * marginPct / 100);
        exGstCents = totalCostCents + marginAmountCents;
    }

    if (!overrideProvided) {
        gstCents = gstExempt ? 0 : Math.round(exGstCents * gstRate);
        incGstCents = exGstCents + gstCents;
    } else if (!incOverrideProvided) {
        gstCents = gstExempt ? 0 : Math.round(exGstCents * gstRate);
        incGstCents = exGstCents + gstCents;
    }

    // Francis rate-card authority: Sell is a FINAL ex-GST charge-out. It must
    // bypass contingency/overhead/markup but still attract GST exactly once.
    // Add this stream before client-line rounding so the visible item rounds as
    // one price. Legacy inc-GST final rows keep their historical post-rounding
    // passthrough below.
    const finalExAddonCents = (!overrideProvided && inputs.finalExGstAddon != null)
        ? toCents(inputs.finalExGstAddon)
        : 0;
    if (finalExAddonCents > 0) {
        const addonGstCents = gstExempt ? 0 : Math.round(finalExAddonCents * gstRate);
        exGstCents += finalExAddonCents;
        gstCents += addonGstCents;
        incGstCents += finalExAddonCents + addonGstCents;
    }

    if (config.roundItemIncGst && !overrideProvided) {
        const roundedIncCents = roundUpToUnitCents(incGstCents, config.itemRoundUnit ?? DEFAULT_ITEM_ROUND_UNIT);
        if (roundedIncCents > incGstCents) {
            if (gstExempt) {
                exGstCents = roundedIncCents;
                gstCents = 0;
            } else {
                exGstCents = Math.round(roundedIncCents / (1 + gstRate));
                gstCents = roundedIncCents - exGstCents;
            }
            incGstCents = roundedIncCents;
            marginAmountCents = exGstCents - totalCostCents;
            marginPct = totalCostCents > 0 ? (marginAmountCents / totalCostCents) * 100 : 0;
        }
    }

    // Slice 0 (rate-final-no-markup, DEC-PE-008): a portion of this line may be
    // a rate-book-matched FINAL inc-GST price that must NOT be marked up. The
    // cost components carry only the to-mark-up remainder; the matched portion
    // arrives as `finalIncGstAddon` and is added AFTER the markup stack +
    // rounding so it passes through at book value. Non-override path only;
    // default absent = no-op (bit-identical). See docs/pricing-engine/SLICE-0-COUNCIL-REVIEW.md.
    const finalAddonCents = (!overrideProvided && inputs.finalIncGstAddon != null)
        ? toCents(inputs.finalIncGstAddon)
        : 0;
    if (finalAddonCents > 0) {
        const addonExCents = gstExempt ? finalAddonCents : Math.round(finalAddonCents / (1 + gstRate));
        incGstCents += finalAddonCents;
        exGstCents += addonExCents;
        gstCents += (finalAddonCents - addonExCents);
    }

    return {
        id: inputs.id,
        title: inputs.title,
        direct: toDollars(directCents),
        contingencyAmount: toDollars(contingencyCents),
        overheadAmount: toDollars(overheadCents),
        totalCost: toDollars(totalCostCents),
        marginPct: Math.round(marginPct * 100) / 100,
        marginAmount: toDollars(marginAmountCents),
        exGst: toDollars(exGstCents),
        gst: toDollars(gstCents),
        incGst: toDollars(incGstCents),
        amount: toDollars(exGstCents), // canonical amount = ex-GST
        amountLocked,
        provisionalSum,
        gstExempt,
        hbcfApplicable: !!inputs.hbcfApplicable,
        excludeFromHbcfEligibility: !!inputs.excludeFromHbcfEligibility,
        trace: {
            direct: toDollars(directCents),
            contingency: toDollars(contingencyCents),
            overhead: toDollars(overheadCents),
            margin: toDollars(marginAmountCents),
            prelims: 0, // prelims modelled as a normal line item today
        },
    };
}

// ---------------- project totals ----------------
/**
 * Aggregate line items into project-level totals.
 * @param {LineItemInputs[]} items
 * @param {ProjectCostConfig} [config]
 * @returns {ProjectTotals}
 */
export function calculateProjectTotals(items = [], config = {}) {
    const fixedAllocationCount = Math.max(1, items.filter(
        raw => raw && raw.excludeFromFixedAllocations !== true,
    ).length);
    const fixedAllocations = config.overhead?.mode === 'fixed'
        ? allocateFixedAmountCents(config.overhead.fixedAmount, fixedAllocationCount)
        : null;
    const fixedContingencyAllocations = config.contingency?.mode === 'fixed'
        ? allocateFixedAmountCents(config.contingency.fixedAmount, fixedAllocationCount)
        : null;
    let fixedAllocationIndex = 0;
    const gstRate = Number.isFinite(config.gstRate) ? config.gstRate : GST_RATE;
    const computed = items.map((raw) => {
        const eligibleForFixedAllocation = raw && raw.excludeFromFixedAllocations !== true;
        const slot = eligibleForFixedAllocation ? fixedAllocationIndex++ : -1;
        let fixedOverheadCents;
        if (fixedAllocations != null) {
            fixedOverheadCents = slot >= 0 ? fixedAllocations[slot] : 0;
        }
        let fixedContingencyCents;
        if (fixedContingencyAllocations != null) {
            fixedContingencyCents = slot >= 0 ? fixedContingencyAllocations[slot] : 0;
        }
        return calculateLineItem(raw, config, {
            itemCount: raw?.excludeFromFixedAllocations === true ? 1 : fixedAllocationCount,
            fixedOverheadCents,
            fixedContingencyCents,
        });
    });

    // DATA-10: default GST is computed once on the ex-GST subtotal. When
    // client-facing item rounding is enabled, each item owns its rounded
    // inc-GST price, so GST is summed from those rounded line totals to keep
    // row amounts and quote totals identical.
    let subtotalExCents = 0;
    let taxableExCents = 0;
    let traceDirect = 0, traceCont = 0, traceOH = 0, traceMargin = 0;

    for (const it of computed) {
        subtotalExCents += toCents(it.exGst);
        if (!it.gstExempt) taxableExCents += toCents(it.exGst);
        traceDirect += toCents(it.trace.direct);
        traceCont   += toCents(it.trace.contingency);
        traceOH     += toCents(it.trace.overhead);
        traceMargin += toCents(it.trace.margin);
    }
    const subtotalGstCents = config.roundItemIncGst
        ? computed.reduce((sum, it) => sum + toCents(it.gst), 0)
        : Math.round(taxableExCents * gstRate);
    const subtotalIncCents = subtotalExCents + subtotalGstCents;

    const totals = {
        items: computed,
        subtotalExGst: toDollars(subtotalExCents),
        subtotalIncGst: toDollars(subtotalIncCents),
        gstAmount: toDollars(subtotalGstCents),
        totalExGst: toDollars(subtotalExCents),
        totalIncGst: toDollars(subtotalIncCents),
        trace: {
            direct: toDollars(traceDirect),
            contingency: toDollars(traceCont),
            overhead: toDollars(traceOH),
            margin: toDollars(traceMargin),
            prelims: 0,
        },
        invariants: { ok: true, failures: [] },
    };

    totals.invariants = checkInvariants(totals, items, config);
    return totals;
}

// ---------------- rounding ----------------
function roundUpLogicalCents(incGstCents) {
    if (!Number.isFinite(incGstCents) || incGstCents <= 0) return 0;
    const unit = incGstCents >= LOGICAL_ROUND_BIG_THRESHOLD * CENTS
        ? LOGICAL_ROUND_BIG_UNIT * CENTS
        : LOGICAL_ROUND_SMALL_UNIT * CENTS;
    return Math.ceil(incGstCents / unit) * unit;
}

/**
 * Round project totalIncGst UP to a logical quote figure; absorb delta
 * into the largest unlocked, non-provisionalSum item. Rebuilds that
 * item's marginAmount/marginPct so its internal math stays consistent.
 * @param {ProjectTotals} totals
 * @param {ProjectCostConfig} [config]
 * @returns {{ totals: ProjectTotals, absorbedIntoItemId: string|null, delta: number, allLocked?: boolean }}
 */
export function applyLogicalRounding(totals, config = {}) {
    if (!totals || !Array.isArray(totals.items) || totals.items.length === 0) {
        return { totals, absorbedIntoItemId: null, delta: 0 };
    }
    const gstRate = Number.isFinite(config.gstRate) ? config.gstRate : GST_RATE;
    const currentIncCents = toCents(totals.totalIncGst);
    const targetIncCents  = roundUpLogicalCents(currentIncCents);
    const deltaIncCents   = targetIncCents - currentIncCents;

    if (deltaIncCents <= 0) return { totals, absorbedIntoItemId: null, delta: 0 };

    // pick largest unlocked non-provisionalSum item
    let pickIdx = -1;
    let pickCents = -Infinity;
    for (let i = 0; i < totals.items.length; i++) {
        const it = totals.items[i];
        if (it.amountLocked || it.provisionalSum) continue;
        const c = toCents(it.exGst);
        if (c > pickCents) { pickCents = c; pickIdx = i; }
    }
    if (pickIdx < 0) {
        return { totals, absorbedIntoItemId: null, delta: 0, allLocked: true };
    }

    const target = totals.items[pickIdx];
    const newIncCents = toCents(target.incGst) + deltaIncCents;
    const newExCents = target.gstExempt
        ? newIncCents
        : Math.round(newIncCents / (1 + gstRate));
    const totalCostCents = toCents(target.totalCost);
    const newMarginCents = newExCents - totalCostCents;
    const newMarginPct = totalCostCents > 0 ? (newMarginCents / totalCostCents) * 100 : 0;
    const newGstCents = target.gstExempt ? 0 : newIncCents - newExCents;

    const rebuilt = {
        ...target,
        exGst: toDollars(newExCents),
        amount: toDollars(newExCents),
        gst: toDollars(newGstCents),
        incGst: toDollars(newIncCents),
        marginAmount: toDollars(newMarginCents),
        marginPct: Math.round(newMarginPct * 100) / 100,
        trace: { ...target.trace, margin: toDollars(newMarginCents) },
    };

    const newItems = totals.items.slice();
    newItems[pickIdx] = rebuilt;

    // Recompute project totals from rebuilt items without re-deriving the
    // rounded line. This keeps the rounded inc-GST target authoritative and
    // reverse-calculates the visible workings back from that final price.
    let subEx = 0, subGst = 0, tDirect = 0, tCont = 0, tOH = 0, tMargin = 0;
    for (const it of newItems) {
        subEx   += toCents(it.exGst);
        subGst  += toCents(it.gst);
        tDirect += toCents(it.trace.direct);
        tCont   += toCents(it.trace.contingency);
        tOH     += toCents(it.trace.overhead);
        tMargin += toCents(it.trace.margin);
    }
    const subInc = subEx + subGst;

    const nextTotals = {
        ...totals,
        items: newItems,
        subtotalExGst: toDollars(subEx),
        subtotalIncGst: toDollars(subInc),
        gstAmount: toDollars(subGst),
        totalExGst: toDollars(subEx),
        totalIncGst: toDollars(subInc),
        trace: {
            direct: toDollars(tDirect),
            contingency: toDollars(tCont),
            overhead: toDollars(tOH),
            margin: toDollars(tMargin),
            prelims: 0,
        },
    };
    nextTotals.invariants = checkInvariants(nextTotals, newItems, config);

    return {
        totals: nextTotals,
        absorbedIntoItemId: target.id ?? null,
        delta: toDollars(deltaIncCents),
    };
}

// ---------------- invariants ----------------
/**
 * @typedef {Object} InvariantResult
 * @property {string} id         invariant code (INV1..INV7)
 * @property {'error'|'warn'} severity  DATA-6: Stage 3 surfaces `error` as a blocker chip
 * @property {string} message    human-readable failure reason
 */

/**
 * Check the 7 canonical invariants. Returns failures, never throws.
 *
 * DATA-6: the return shape now includes `results[]` with a severity per
 * failure so Stage 3 can surface a blocker chip for `error`-level failures
 * without re-parsing the legacy `failures[]` string array. Legacy
 * `failures[]` is preserved for back-compat with callers that string-match.
 *
 * @param {ProjectTotals} totals
 * @param {LineItemInputs[]} [rawItems]
 * @param {ProjectCostConfig} [config]
 * @returns {{ ok: boolean, failures: string[], results: InvariantResult[] }}
 */
export function checkInvariants(totals, rawItems = [], config = {}) {
    const results = [];
    const push = (id, severity, message) => results.push({ id, severity, message });
    if (!totals || !Array.isArray(totals.items)) {
        push('INV-shape', 'error', 'totals missing items[]');
        return { ok: false, failures: results.map((r) => `${r.id}: ${r.message}`), results };
    }
    const gstRate = Number.isFinite(config.gstRate) ? config.gstRate : GST_RATE;

    // INV1: all cost inputs ≥ 0
    for (let i = 0; i < rawItems.length; i++) {
        const r = rawItems[i] || {};
        const fields = ['materialsCost', 'labourCost', 'equipmentCost', 'subcontractCost', 'otherCost'];
        for (const f of fields) {
            if (parseAmount(r[f]) < 0) push('INV1', 'error', `item[${i}].${f} < 0`);
        }
    }

    // INV2: exGst > 0 if direct > 0
    for (let i = 0; i < totals.items.length; i++) {
        const it = totals.items[i];
        if (it.direct > 0 && it.exGst <= 0) push('INV2', 'error', `item[${i}] direct>0 but exGst<=0`);
    }

    // INV3: |incGst - exGst * (1+gst)| ≤ 1c per item (DATA-2: gstExempt lines expect incGst === exGst)
    for (let i = 0; i < totals.items.length; i++) {
        const it = totals.items[i];
        const expectedIncCents = it.gstExempt
            ? toCents(it.exGst)
            : toCents(it.exGst) + Math.round(toCents(it.exGst) * gstRate);
        if (Math.abs(toCents(it.incGst) - expectedIncCents) > EPSILON_CENTS) {
            push('INV3', 'error', `item[${i}] incGst≠exGst×(1+gst)`);
        }
    }

    // INV4: Σ items.exGst ≈ subtotalExGst
    const sumItemEx = totals.items.reduce((s, it) => s + toCents(it.exGst), 0);
    if (Math.abs(sumItemEx - toCents(totals.subtotalExGst)) > EPSILON_CENTS) {
        push('INV4', 'error', 'Σitems.exGst ≠ subtotalExGst');
    }

    // INV5: HBCF present when the pre-HBCF subtotalIncGst is strictly above
    // the threshold. Exactly $20,000 inc GST does not trigger HBCF.
    // DATA-7: removed `anyApplicable` precondition: the guard meant that
    // when the AI forgot to mark any line `hbcfApplicable`, the invariant
    // trivially passed (the exact miss case it should catch). We now always
    // flag over-threshold quotes that lack an HBCF line.
    const hbcfEligibilityBaseIncGstCents = totals.items.reduce((sum, item) => {
        const statutoryRow = STATUTORY_INSURANCE_RE.test(item.id || '')
            || STATUTORY_INSURANCE_RE.test(item.title || '');
        return item.excludeFromHbcfEligibility || statutoryRow ? sum : sum + toCents(item.incGst);
    }, 0);
    const configuredThresholdInput = config?.hbcf?.thresholdIncGst ?? config?.hbcf?.threshold_inc_gst;
    const configuredHbcfThreshold = Number(
        String(configuredThresholdInput ?? '').replace(/[$,\s]/g, ''),
    );
    const hbcfThresholdIncGst = configuredThresholdInput !== undefined
        && configuredThresholdInput !== null
        && configuredThresholdInput !== ''
        && Number.isFinite(configuredHbcfThreshold)
        && configuredHbcfThreshold >= 0
        ? configuredHbcfThreshold
        : HBCF_THRESHOLD_INCGST;
    if (hbcfEligibilityBaseIncGstCents > toCents(hbcfThresholdIncGst)) {
        const hasHbcf = totals.items.some((it) => it.hbcfApplicable
            || STATUTORY_INSURANCE_RE.test(it.id || '')
            || STATUTORY_INSURANCE_RE.test(it.title || ''));
        if (!hasHbcf) push('INV5', 'error', 'HBCF line missing above threshold');
    }

    // INV6: trace components sum to exGst within 1c (project-level)
    const traceSum = toCents(totals.trace.direct)
        + toCents(totals.trace.prelims)
        + toCents(totals.trace.overhead)
        + toCents(totals.trace.contingency)
        + toCents(totals.trace.margin);
    if (Math.abs(traceSum - toCents(totals.totalExGst)) > EPSILON_CENTS) {
        push('INV6', 'error', 'trace components ≠ totalExGst');
    }

    // INV7: skipped here. Rounding is caller-opt-in. applyLogicalRounding enforces
    // its own delta bound. When caller wants to verify post-rounding, compare
    // pre/post totalIncGst externally; we still sanity-check delta when present.

    const failures = results.map((r) => `${r.id}: ${r.message}`);
    return { ok: failures.length === 0, failures, results };
}
